import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class EncodingService {
  // Character -> token mapping
  private readonly encodingMap: Record<string, string> = {
    '#': '__HASH__',
    '&': '__AMP__',
    '<': '__LT__',
    '>': '__GT__',
    '"': '__QUOTE__',
    "'": '__APOS__',
    '%': '__PERCENT__',
    '+': '__PLUS__',
    '?': '__QUESTION__',
    '=': '__EQUALS__',
    '/': '__SLASH__',
    '\\': '__BACKSLASH__',
    '`': '__BACKTICK__',
    '$': '__DOLLAR__',
    ';': '__SEMICOLON__',
    '.': '__DOT__'
  };

  // Token -> character mapping (derived)
  private readonly decodingMap: Record<string, string>;

  constructor() {
    const reverse: Record<string, string> = {};
    for (const k of Object.keys(this.encodingMap)) {
      reverse[this.encodingMap[k]] = k;
    }
    this.decodingMap = reverse;
  }

  /**
   * Encode special characters in a string.
   */
  encode(value: string): string {
    if (!value || typeof value !== 'string') return value;

    let encoded = value;
    for (const char of Object.keys(this.encodingMap)) {
      const regex = new RegExp(this.escapeRegExp(char), 'g');
      encoded = encoded.replace(regex, this.encodingMap[char]);
    }

    return encoded;
  }

  /**
   * Decode tokens back into their original characters.
   */
  decode(value: string): string {
    if (!value || typeof value !== 'string') return value;

    let decoded = value;
    for (const token of Object.keys(this.decodingMap)) {
      const regex = new RegExp(this.escapeRegExp(token), 'g');
      decoded = decoded.replace(regex, this.decodingMap[token]);
    }

    return decoded;
  }

  /**
   * Encode all string properties in an object/array (deep).
   * Leaves Date instances unchanged.
   */
  encodeObject<T>(obj: T): T {
    return this.transformObject(obj, (s) => this.encode(s));
  }

  /**
   * Decode all string properties in an object/array (deep).
   * Leaves Date instances unchanged.
   */
  decodeObject<T>(obj: T): T {
    return this.transformObject(obj, (s) => this.decode(s));
  }

  private transformObject<T>(obj: T, transform: (value: string) => string): T {
    if (obj === null || obj === undefined) return obj;

    // Keep Date objects unchanged
    if (obj instanceof Date) return obj;

    // Arrays
    if (Array.isArray(obj)) {
      return obj.map((item) => this.transformObject(item, transform)) as unknown as T;
    }

    // Plain objects
    if (typeof obj === 'object') {
      const out: any = { ...(obj as any) };
      for (const key of Object.keys(out)) {
        const v = out[key];
        if (typeof v === 'string') {
          out[key] = transform(v);
        } else if (v instanceof Date) {
          out[key] = v;
        } else if (v && typeof v === 'object') {
          out[key] = this.transformObject(v, transform);
        }
      }
      return out as T;
    }

    // Primitives (number/boolean/function/etc)
    return obj;
  }

  private escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}
